import { useState } from "react";
import SubscriptionBookingModal from "../SubscriptionBookingModal/SubscriptionBookingModal";
import Map from "../Map/Map";
import { useLanguage } from "../../context/LanguageContext";

// Translation object with all the text content in both Arabic and English
const SubscriptionCard = ({ subscription }) => {
  const { t, language } = useLanguage();
  // State for modal visibility
  const [showBookingModal, setShowBookingModal] = useState(false);

  const [editingAddress, setEditingAddress] = useState(null);
  const [openMap, setOpenMap] = useState(false);
  const [showAddressWarning, setShowAddressWarning] = useState(false);

  // Format the date in both Arabic and English
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const arOptions = { year: "numeric", month: "long", day: "numeric" };
    const enOptions = { year: "numeric", month: "long", day: "numeric" };

    const arDate = date.toLocaleDateString("ar-SA", arOptions);
    const enDate = date.toLocaleDateString("en-US", enOptions);

    return { ar: arDate, en: enDate };
  };

  const getRemainingDays = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = Math.abs(end - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Check if subscription is expired
  const isExpired = new Date(subscription.expireAt) < new Date();
  // Get remaining days
  const remainingDays = isExpired ? 0 : getRemainingDays(subscription.expireAt);
  // Get package name based on language
  const packageName =
    language === "ar"
      ? subscription.package?.name?.ar
      : subscription.package?.name?.en || "Subscription";
  // Get package features
  const packageFeatures = subscription.package?.features || [];
  // Calculate remaining percentage for progress circle
  const totalDays = 365; // Assuming annual subscription
  const remainingPercentage = (remainingDays / totalDays) * 100;
  // Set colors: active is green, expired is silver
  const cardColor = isExpired ? "#C0C0C0" : "#5cb85c";

  // Handle booking request
  const handleRequestVisit = () => {
    setShowBookingModal(true);
  };

  // Handle booking complete
  const handleBookingComplete = () => {
    setShowBookingModal(false);
  };
  // Handle address being edited
  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setShowAddressWarning(true);
  };
  // Handle continue editing after warning
  const handleContinueEditing = () => {
    setShowAddressWarning(false);
    setOpenMap(true);
  };
  // Handle cancel editing
  const handleCancelEditing = () => {
    setShowAddressWarning(false);
    setEditingAddress(null);
  };

  const handleMapClose = () => {
    setOpenMap(false);
    setEditingAddress(null);
  };

  return (
    <>
      <div
        className={`subscription-card package ${
          isExpired ? "expired" : "active"
        }`}
        style={{ borderColor: cardColor }}
      >
        <div
          className="subscription-header"
          style={{ backgroundColor: cardColor }}
        >
          <h3 className="subscription-name">
            {packageName || t("specialPackage")}
          </h3>
          <div className="subscription-status">
            {isExpired ? t("expired") : t("active")}
          </div>
        </div>

        <div className="subscription-body">
          <div className="subscription-dates">
            <div className="date-item">
              <span className="date-label">{t("startDate")}</span>
              <span className="date-value">
                <div className="ar-date">
                  {formatDate(subscription.createdAt).ar}
                </div>
                <div className="en-date">
                  {formatDate(subscription.createdAt).en}
                </div>
              </span>
            </div>
            <div className="date-item">
              <span className="date-label">{t("endDate")}</span>
              <span className="date-value">
                <div className="ar-date">
                  {formatDate(subscription.expireAt).ar}
                </div>
                <div className="en-date">
                  {formatDate(subscription.expireAt).en}
                </div>
              </span>
            </div>
          </div>
          <div className="subscription-address">
            <div className="title-update-action">
              <h3>{t("subscriptionAddress")}</h3>
              <button
                type="button"
                className={`active-btn`}
                onClick={() => handleEditAddress(subscription?.address)}
              >
                {t("requestEdit")}
              </button>
            </div>
       <span>
  <strong>{subscription?.address?.name}</strong>, {`${subscription?.address?.street}`}
</span>
          </div>

          <div className="subscription-remaining">
            <div
              className="remaining-circle"
              style={{
                background: isExpired
                  ? "#eee"
                  : `conic-gradient(${cardColor} ${remainingPercentage}%, #eee 0)`,
              }}
            >
              <div className="remaining-inner">
                {isExpired ? (
                  <div className="expired-label">{t("expired")}</div>
                ) : (
                  <>
                    <span className="days-number">{remainingDays}</span>
                    <span className="days-text">{t("daysRemaining")}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {packageFeatures?.length > 0 && (
            <div className="subscription-features">
              <h4 className="features-title">{t("packageIncludes")}</h4>
              <ul className="features-list">
                {packageFeatures.map((feature, idx) => (
                  <li key={idx} className="feature-item">
                    <span className="feature-check">✓</span>
                    {language === "ar" ? feature.ar : feature.en}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="subscription-price">
            <span className="price-value">
              {subscription.package?.price ||
                subscription.subscriptionDetails.price}
            </span>
            <span className="price-currency">
              {language === "ar" ? "ر.س" : "SAR"}
            </span>
            <span className="price-period">
              /
              {subscription.package?.type === "annually"
                ? t("year")
                : t("month")}
            </span>
          </div>
        </div>

        <div className="subscription-actions single-action">
          {isExpired ? (
            <button className="action-btn renew-full">
              {t("renewSubscription")}
            </button>
          ) : (
            <button
              className="action-btn request-visit"
              onClick={handleRequestVisit}
            >
              {t("requestVisit")}
            </button>
          )}
        </div>
      </div>

      {/* Subscription Booking Modal */}
      <SubscriptionBookingModal
        open={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        subscription={subscription}
        onBookingComplete={handleBookingComplete}
      />

      {/* Address Warning Modal */}
      {showAddressWarning && (
        <div className="modal-overlay active">
          <div className="modal address-warning-modal">
            <div className="modal-header">
              <h3>{t("warning")}</h3>
              <button className="close-btn" onClick={handleCancelEditing}>
                ×
              </button>
            </div>
            <div className="modal-content">
              <p className="warning-text ">{t("addressWarningText")}</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn secondary-btn"
                onClick={handleCancelEditing}
              >
                {t("cancel")}
              </button>
              <button
                className="btn primary-btn"
                onClick={handleContinueEditing}
              >
                {t("ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Open Map To Edit Address */}
      <Map
        open={openMap}
        onClose={handleMapClose}
        editAddress={editingAddress}
        packageData={subscription}
      />
    </>
  );
};

export default SubscriptionCard;
